import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import styled from 'styled-components';

import { SelectPidTypesV2 } from '@/entities/personalIdentifiers/components/SelectPidTypesV2';
import { CountriesList } from '@/shared/dictionaries/countries';
import { useFieldAction } from '@/shared/lib/hooks/useFieldAction';
import { SelectFromList } from '@/shared/lib/hooks/useFormRenderV2/components/SelectFromList';
import { SelectPidBeneficiaryV2 } from '@/shared/lib/hooks/useFormRenderV2/components/SelectPidBeneficiaryV2';
import { FieldActionType } from '@/shared/lib/hooks/useFormRenderV2/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import {
  PersonalIdentifierDocumentDto,
  PersonalIdentifierType,
} from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { CountryCode } from '@/shared/types';
import { Title } from '@/shared/uikit/Title';
import { Input } from '@/shared/uikitV2/Input';
import { Option } from '@/shared/uikitV2/Select/types';
import { FileUploader, FileUploaderRef, UploadState } from '@/widgets/FileUploader';

import { SelectOptions } from '../../components/SelectItem/types';

// PersonalIdentifierDocumentDto
export type FormFields = {
  personalIdentifierType: PersonalIdentifierType;
  beneficiaryId: DtoId | 'me';
  name: string;
  names: string;
  surnames: string;
  country: CountryCode;
};

type CreatePidProps = {
  formId: string;
  onChangeState: (uploading: boolean) => void;
  onCreate: (data: SelectOptions) => void;
};

// TODO rework components Create*
export const CreatePid: FC<CreatePidProps> = ({ formId, onChangeState, onCreate }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectCollectionItem' });
  const { t: tf } = useTranslation('forms_personal_ids', { keyPrefix: 'common.inputs' });
  const { keyPair } = useKeyPairStore();
  const sjdb = useSJDatabase();
  const uploaderRef = useRef<FileUploaderRef>(null);
  const [uploadState, setUploadState] = useState<UploadState | null>();

  const handleSubmit = async ({ ...data }: FormFields) => {
    if (!keyPair || !uploaderRef.current) {
      return;
    }

    const res = await uploaderRef.current.upload(true);
    const pidDto: PersonalIdentifierDocumentDto = {
      id: sjdb.generateId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS),
      type: DocumentType.PERSONAL_IDENTIFIERS,
      ...data,
    };

    if (res.success) {
      pidDto.attachments = res.filePaths;
    }

    const pid = await sjdb.collections.personalIdentifiersDocuments?.create(pidDto, [
      keyPair.publicKey,
    ]);

    if (!pid) return;

    onCreate({
      colName: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
      id: pid.id,
    });
  };

  const { control, handleSubmit: formHandleSubmit, setValue } = useForm<FormFields>();

  const handleFieldAction = useFieldAction({
    fieldName: 'beneficiaryId',
    setValue,
    fieldActionType: FieldActionType.ASSIGN_TO_MYSELF,
  });

  const countries: Option[] = useMemo(
    () =>
      CountriesList.map((option) => ({
        value: option.key,
        content: { label: option.label },
      })),
    [],
  );

  useEffect(() => {
    onChangeState(Boolean(uploadState?.uploading));
  }, [uploadState?.uploading]);

  return (
    <Container>
      <Form style={{ display: 'contents' }} id={formId} onFinish={formHandleSubmit(handleSubmit)}>
        <Controller
          name="personalIdentifierType"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <SelectPidTypesV2
              label="Type"
              errorText={fieldState.error?.message}
              status={fieldState.error && 'error'}
              required
              {...field}
            />
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Input
              errorText={fieldState.error?.message}
              status={fieldState.error && 'error'}
              label={tf('title.label')}
              required
              {...field}
            />
          )}
        />

        <Controller
          name="names"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Input
              errorText={fieldState.error?.message}
              status={fieldState.error && 'error'}
              label={tf('firstName.label')}
              required
              {...field}
            />
          )}
        />

        <Controller
          name="surnames"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Input
              errorText={fieldState.error?.message}
              status={fieldState.error && 'error'}
              label={tf('surname.label')}
              required
              {...field}
            />
          )}
        />

        <SelectFromList
          name="country"
          control={control}
          options={countries}
          label={tf('country.label')}
          required
          withSearch={true}
        />

        <SelectPidBeneficiaryV2
          name="beneficiaryId"
          buttonAfterConfig={{
            label: 'associatedWith.button',
            action: FieldActionType.ASSIGN_TO_MYSELF,
          }}
          buttonAfterLabel={tf('associatedWith.button')}
          control={control}
          handleFieldAction={handleFieldAction}
          label={tf('associatedWith.label')}
          required
        />
      </Form>
      <Title size={16} bold>
        {t('filesTitle')}
      </Title>
      <FileUploader onUpdate={setUploadState} ref={uploaderRef} />
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  flex-direction: column;
  gap: 16px;
`;
